import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers.ts';
import { ReminderIntervalsForm } from 'types/tenants.ts';
import { t } from 'utils/getTranslations';
import { getClassifierText } from 'utils/getTranslations.ts';

interface Props {
    classifiers: Record<string, ClassifierData>;
    initialData: ReminderIntervalsForm;
    onSubmit: (data: ReminderIntervalsForm) => void;
    onClose: () => void;
}

const ReminderIntervalsEditModal = (props: Props) => {
    const { initialData, onClose, onSubmit, classifiers } = props;
    const errorIdPrefix = useId();

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<ReminderIntervalsForm>({
        defaultValues: initialData,
    });

    const onSave = () => {
        void handleSubmit((data) => onSubmit(data))();
    };

    return (
        <>
            <ModalHeader title={t('configuration.debt-collection')} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-[23px] pt-[15px] pb-[45px]">
                <div>
                    <h6 className="text-h6 mb-2">
                        <FormattedMessage id="configuration.edit-reminder-intervals" />
                    </h6>
                    <p className="text-body2 text-fg-secondary mb-2">
                        <FormattedMessage id="configuration.reminder-intervals-help-text" />
                    </p>
                    <div className="border-divider overflow-hidden rounded-[4px] border">
                        <table className="w-full text-left text-sm">
                            <TableHeader className="[&_tr]:border-b-0">
                                <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                    <TableHead className={SIMPLE_TABLE_HEAD_CLASS}>
                                        <FormattedMessage id="configuration.reminder-step" />
                                    </TableHead>
                                    <TableHead className={SIMPLE_TABLE_HEAD_CLASS}>
                                        <FormattedMessage id="configuration.days-until-next" />
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(initialData.debtCollectionReminderIntervals || []).map((interval, index) => {
                                    const stepLabel = getClassifierText(
                                        classifiers['DEBT_REMINDER_TYPE'],
                                        interval.debtReminderType
                                    );
                                    const error = errors.debtCollectionReminderIntervals?.[index]?.days;
                                    const errorId = `${errorIdPrefix}-${index}-error`;
                                    return (
                                        <TableRow key={interval.debtReminderType} className={SIMPLE_TABLE_ROW_CLASS}>
                                            <TableCell className={SIMPLE_TABLE_CELL_CLASS}>{stepLabel}</TableCell>
                                            <TableCell className={SIMPLE_TABLE_CELL_CLASS}>
                                                <Controller
                                                    name={`debtCollectionReminderIntervals.${index}.days`}
                                                    control={control}
                                                    rules={{
                                                        required: t('forms.field-is-required'),
                                                        min: {
                                                            value: 1,
                                                            message: t('configuration.min-days-error'),
                                                        },
                                                        max: {
                                                            value: 365,
                                                            message: t('configuration.max-days-error'),
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-[100px]">
                                                                <Input
                                                                    {...field}
                                                                    type="number"
                                                                    min={1}
                                                                    max={365}
                                                                    aria-label={stepLabel}
                                                                    aria-invalid={!!error}
                                                                    aria-describedby={
                                                                        error?.message ? errorId : undefined
                                                                    }
                                                                    className="bg-paper"
                                                                />
                                                                <FieldError
                                                                    id={errorId}
                                                                    className="text-caption text-error mx-3.5 mt-[3px]"
                                                                    errors={[error]}
                                                                />
                                                            </div>
                                                            <p className="text-body2">
                                                                <FormattedMessage id="configuration.days" />
                                                            </p>
                                                        </div>
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </table>
                    </div>
                </div>
            </div>
            <ModalFooter onCancel={onClose} onSave={onSave} />
        </>
    );
};

export default ReminderIntervalsEditModal;
